import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import isEqual from 'lodash/isEqual';

import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';
import {
  observeDocumentSettings,
  readDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

import { useStore } from 'utils/hooks/store';

import { DocumentSettingsFormValues } from './types';
import { toFormValues } from './useDocumentSettingsForm';

const RemoteSettingsObserver: React.FC = () => {
  const { dirty, resetForm, initialValues } = useFormikContext<DocumentSettingsFormValues>();
  const { ydoc } = useEditorStore();
  const { notification } = useStore();

  const dirtyRef = useRef(dirty);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const initialValuesRef = useRef(initialValues);
  useEffect(() => {
    initialValuesRef.current = initialValues;
  }, [initialValues]);

  useEffect(() => {
    if (!ydoc) return undefined;
    return observeDocumentSettings(ydoc, ({ isLocal }) => {
      if (isLocal) return;
      const next = toFormValues(readDocumentSettings(ydoc));
      // A view-mode switch recreates the Y.Doc, which re-syncs our own just-saved
      // settings as a non-local change; ignore it when it matches the saved baseline.
      if (isEqual(next, initialValuesRef.current)) return;
      const wasDirty = dirtyRef.current;
      resetForm({ values: next });
      if (wasDirty) {
        notification.enqueueWarningSnackbar('Settings changed by another user. Your unsaved changes were discarded.');
      }
    });
  }, [ydoc, resetForm, notification]);

  return null;
};

export default RemoteSettingsObserver;
